import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { CrmHttpError, describeCrmError } from "../../../api/crm";
import {
  canPlayRecording,
  createLeadComment,
  fetchLeadCommentAttachmentObjectUrl,
  getEmailBodyHtml,
  getEmailContentUrl,
  getLeadTimeline,
  setLeadCommentArchived,
  updateLeadComment,
} from "../../../api/crmTimeline";
import { sanitizeEmailHtml } from "../../../api/sanitizeEmailHtml";
import {
  Comment,
  CommentKind,
  EmailStatus,
  TimelineCommentEntry,
  TimelineEmailEntry,
  TimelineEntry,
  TimelineMilestoneEntry,
  TimelineMoveEntry,
  TimelineObservationEntry,
} from "../../../api/crmTypes";
import CategoryChipBar, {
  CategoryChipOption,
} from "../../../components/crm/CategoryChipBar";
import { useCrmCursorList } from "../../../hooks/useCrmResource";
import {
  CrmConnectGate,
  useCrmSession,
} from "../../../context/CrmSessionContext";
import { useAlert, useConfirm } from "../../../components/Dialog";
import { fmtDateTime } from "../../../types/promos";
import {
  AttachmentPickerUI,
  formatBytes,
  useAttachmentPicker,
} from "../../../components/crm/attachmentPicker";
import NoteBody from "../../../components/crm/NoteBody";
import NoteEditor, { useNoteComposer } from "../../../components/crm/NoteEditor";
import RecordingPlayer from "../../../components/crm/RecordingPlayer";
import RichNoteBody from "../../../components/crm/RichNoteBody";
import { PrimaryButton } from "../../../components/FormControls";
import { IconClose } from "../../../components/Icons";
import Section from "./Section";
import Pill, { PillScheme } from "./Pill";

/**
 * Panel "Actividad (CRM)" del aside de la ficha del lead (Fase 3 de la
 * integración CRM). Renderiza la cronología del lead servida por el BFF del CRM
 * (GET /leads/:id/timeline) como un timeline vertical con paginación por cursor,
 * play de grabaciones de llamada y un composer para añadir notas internas.
 *
 * El CRM usa el MISMO ULID del lead que Operations como PK, así que pasamos
 * `leadUlid` tal cual. El endpoint nunca devuelve 404 para un lead desconocido:
 * un lead sin proyectar devuelve items/pinned vacíos (estado vacío). Un id con
 * forma inválida sí es 400 (imposible aquí: el ULID viene de la ficha).
 *
 * Emails: mostramos asunto + estado del ciclo de vida; al expandir, el cuerpo
 * HTML se pide on-demand (GET /leads/:id/emails/:emailId/html — el listado solo
 * trae el flag hasBodyHtml) y se renderiza SANITIZADO dentro de un iframe
 * aislado (EmailBody).
 */
export default function LeadTimeline({ leadUlid }: { leadUlid: string }) {
  const [maximized, setMaximized] = useState(false);
  // El aside y el overlay son DOS instancias independientes del timeline. Con
  // las mutaciones de nota (editar/archivar/restaurar) el overlay puede dejar
  // al aside desactualizado — y con acciones ya inválidas (editar una nota que
  // acabas de archivar → 409). Al cerrar el overlay se remonta el aside
  // (epoch como key) para que refetchee y refleje lo mutado.
  const [asideEpoch, setAsideEpoch] = useState(0);
  return (
    <>
      <Section
        title="Actividad (CRM)"
        accent="#0ca678"
        defaultOpen
        rightSlot={
          <button
            type="button"
            onClick={() => setMaximized(true)}
            title="Maximizar actividad"
            aria-label="Maximizar actividad"
            style={maximizeBtn}
          >
            <ExpandIcon />
          </button>
        }
      >
        <CrmConnectGate>
          <LeadTimelineInner key={asideEpoch} leadUlid={leadUlid} />
        </CrmConnectGate>
      </Section>

      {maximized && (
        <TimelineOverlay
          onClose={() => {
            setMaximized(false);
            setAsideEpoch((e) => e + 1);
          }}
        >
          <CrmConnectGate>
            <LeadTimelineInner leadUlid={leadUlid} />
          </CrmConnectGate>
        </TimelineOverlay>
      )}
    </>
  );
}

// Overlay grande centrado (~80% del viewport) con scroll propio, cierre por X,
// backdrop y Esc. No reutiliza ModalShell (top-aligned, sin Esc, ancho acotado a
// 96vw); montamos un shell propio simple para el tamaño y el comportamiento que
// pide el panel maximizado.
function TimelineOverlay({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Portal a document.body: renderizado inline dentro del <aside> sticky, el
  // overlay queda atrapado en el stacking context del aside y la cabecera del
  // lead lo tapa (bug 2026-07-14). zIndex 150: sobre toda la UI, bajo los
  // Dialog (200) para que un confirm abierto encima siga ganando.
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 150,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "80vw",
          height: "80vh",
          maxWidth: 1100,
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 10px 32px rgba(0,0,0,.25)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0.9rem 1.1rem",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "1rem",
              color: "#1f2933",
              flex: 1,
              textTransform: "uppercase",
              letterSpacing: ".4px",
            }}
          >
            Actividad (CRM)
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            style={maximizeBtn}
          >
            <IconClose />
          </button>
        </header>
        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ExpandIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

const PAGE_SIZE = 30;

/** Filtro por tipo de evento; espeja el del CRM (`Timeline.tsx`). */
type Category = "todos" | "whatsapp" | "llamadas" | "emails" | "notas" | "crm";

/** Orden y etiquetas de los chips de categoría. */
const CATEGORY_OPTIONS: CategoryChipOption<Category>[] = [
  { key: "todos", label: "Todos" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "llamadas", label: "Llamadas" },
  { key: "emails", label: "Emails" },
  { key: "notas", label: "Notas" },
  { key: "crm", label: "CRM" },
];

/** Clasifica una entrada del timeline en su categoría de filtro. */
function categoryOf(entry: TimelineEntry): Exclude<Category, "todos"> {
  if (
    entry.type === "workflow_move" ||
    entry.type === "external_observation" ||
    entry.type === "lead_milestone"
  )
    return "crm";
  if (entry.type === "email") return "emails";
  switch (entry.kind) {
    case "whatsapp_enviado":
    case "whatsapp_recibido":
      return "whatsapp";
    case "llamada":
      return "llamadas";
    case "email_enviado":
    case "email_recibido":
      return "emails";
    case "nota_interna":
      return "notas";
    default:
      // sistema y futuros kinds → acciones del CRM/sistema
      return "crm";
  }
}

// El leadUlid de la ficha, disponible para las tarjetas del timeline (lo necesita
// EmailBody para pedir el cuerpo on-demand) sin pasarlo por props a través de la
// fila memoizada. Es el id de la ruta de Operations, el mismo ULID que el CRM usa
// como PK del lead, ya canónico.
const TimelineLeadUlidContext = createContext<string | null>(null);

function useTimelineLeadUlid(): string {
  const value = useContext(TimelineLeadUlidContext);
  if (value === null) {
    throw new Error("useTimelineLeadUlid debe usarse dentro del timeline");
  }
  return value;
}

// Recarga del timeline para las mutaciones de nota (editar/archivar/restaurar):
// mismo efecto que la invalidación de query del CRM. Vuelve a la primera página
// (los cambios de una nota vieja se ven al repaginar).
const TimelineReloadContext = createContext<(() => void) | null>(null);

function useTimelineReload(): () => void {
  const value = useContext(TimelineReloadContext);
  if (value === null) {
    throw new Error("useTimelineReload debe usarse dentro del timeline");
  }
  return value;
}

function LeadTimelineInner({ leadUlid }: { leadUlid: string }) {
  // Primera página + "Cargar más" acumulativo por cursor, con guard de
  // cancelación/lead en ambos y reset síncrono de items/cursor al cambiar de
  // lead (evita ver la actividad del lead A en el lead B).
  const fetchPage = useCallback(
    (before?: string) =>
      getLeadTimeline(leadUlid, { limit: PAGE_SIZE, before }),
    [leadUlid],
  );
  const {
    items,
    loading,
    loadingMore,
    error,
    nextCursor,
    reload,
    loadMore,
    setItems,
  } = useCrmCursorList<TimelineEntry>(
    fetchPage,
    [leadUlid],
    "No se pudo cargar la actividad del CRM",
    "No se pudieron cargar más entradas",
  );

  const [category, setCategory] = useState<Category>("todos");
  // El endpoint solo acepta limit/before: el filtro es cliente y aplica a las
  // páginas ya cargadas; "Cargar más" amplía el conjunto filtrable.
  const loaded = useMemo(() => items ?? [], [items]);
  const list = useMemo(
    () =>
      category === "todos"
        ? loaded
        : loaded.filter((e) => categoryOf(e) === category),
    [loaded, category],
  );

  // Al crear una nota, prependemos la entrada localmente en vez de recargar,
  // para no perder las páginas ya cargadas.
  const handleCreated = useCallback(
    (comment: Comment) => {
      setItems((prev) => [commentToTimelineEntry(comment), ...(prev ?? [])]);
    },
    [setItems],
  );

  if (loading && items === null) {
    return <Muted>Cargando actividad…</Muted>;
  }

  if (error && items === null) {
    return (
      <div>
        <ErrorBanner>{error}</ErrorBanner>
        <button type="button" onClick={reload} style={retryBtn}>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <TimelineLeadUlidContext.Provider value={leadUlid}>
      <TimelineReloadContext.Provider value={reload}>
      <div>
        <Composer leadUlid={leadUlid} onCreated={handleCreated} />

      <CategoryChipBar
        options={CATEGORY_OPTIONS}
        value={category}
        onChange={setCategory}
        shown={list.length}
        total={loaded.length}
        allKey="todos"
      />

      {list.length === 0 && (
        <Muted>
          {loaded.length === 0
            ? "Sin actividad registrada."
            : "Sin actividad de este tipo en lo cargado."}
        </Muted>
      )}

      {list.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {list.map((entry, i) => (
            <TimelineRow
              key={entryKey(entry, i)}
              entry={entry}
              last={i === list.length - 1 && !nextCursor}
            />
          ))}
        </ul>
      )}

      {error && items !== null && (
        <div style={{ marginTop: "0.5rem" }}>
          <ErrorBanner>{error}</ErrorBanner>
        </div>
      )}

      {nextCursor && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          style={{ ...retryBtn, marginTop: "0.3rem" }}
        >
          {loadingMore ? "Cargando…" : "Cargar más"}
        </button>
        )}
      </div>
      </TimelineReloadContext.Provider>
    </TimelineLeadUlidContext.Provider>
  );
}

// Clave estable por entrada: cada tipo trae su id natural; el índice desempata
// (dos milestones sintéticos no lo tienen, pero solo hay uno por lead).
function entryKey(e: TimelineEntry, i: number): string {
  switch (e.type) {
    case "comment":
      return `c:${e.commentId}`;
    case "workflow_move":
      // Con moveId (auditoría 2026-07-04) la clave es única. En el fallback
      // legacy sin moveId añadimos fromTag y el índice al desempate: dos moves
      // al mismo toTag con timestamp idéntico ya no colisionan.
      return `m:${
        e.moveId ?? `${e.fromTag ?? ""}:${e.toTag}:${e.occurredAt}:${i}`
      }`;
    case "email":
      return `e:${e.emailId}`;
    case "external_observation":
      return `o:${e.observationId}`;
    case "lead_milestone":
      return `ms:${e.milestoneKind}:${i}`;
  }
}

// Construye la TimelineCommentEntry equivalente al Comment devuelto por el POST
// (201), para prependerla localmente sin recargar. El POST solo crea notas
// internas; los campos de llamada/ticket/fijado/compartido no aplican (ausentes).
function commentToTimelineEntry(c: Comment): TimelineCommentEntry {
  return {
    type: "comment",
    occurredAt: c.createdAt,
    actor: c.actor,
    actorName: c.actorName,
    actorPictureUrl: c.actorPictureUrl,
    commentId: c.id,
    kind: c.kind,
    body: c.body,
    bodyHtml: c.bodyHtml ?? null,
  };
}

// ---------------------------------------------------------------------------
// Fila del timeline: línea vertical + punto + tarjeta por tipo.
// ---------------------------------------------------------------------------

// memo: the list re-renders on every "Cargar más" (new items appended) and on
// each note prepend; already-rendered rows have stable props, so memo skips
// re-rendering them (each card only formats dates / renders pills).
const TimelineRow = memo(function TimelineRow({
  entry,
  last,
}: {
  entry: TimelineEntry;
  last: boolean;
}) {
  const dot = DOT_COLOR[entry.type];
  return (
    <li style={{ display: "flex", gap: "0.6rem" }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 14,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: dot,
            marginTop: 4,
            flexShrink: 0,
          }}
        />
        {!last && (
          <span style={{ flex: 1, width: 2, background: "#e9ecef" }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingBottom: "0.8rem" }}>
        <TimelineCard entry={entry} />
      </div>
    </li>
  );
});

const DOT_COLOR: Record<TimelineEntry["type"], string> = {
  comment: "#7048e8",
  workflow_move: "#1c7ed6",
  email: "#f08c00",
  external_observation: "#495057",
  lead_milestone: "#0ca678",
};

function TimelineCard({ entry }: { entry: TimelineEntry }) {
  switch (entry.type) {
    case "comment":
      return <CommentCard entry={entry} />;
    case "workflow_move":
      return <MoveCard entry={entry} />;
    case "email":
      return <EmailCard entry={entry} />;
    case "external_observation":
      return <ObservationCard entry={entry} />;
    case "lead_milestone":
      return <MilestoneCard entry={entry} />;
  }
}

const COMMENT_KIND_LABELS: Record<CommentKind, string> = {
  nota_interna: "Nota interna",
  llamada: "Llamada",
  email_enviado: "Email enviado",
  email_recibido: "Email recibido",
  whatsapp_enviado: "WhatsApp enviado",
  whatsapp_recibido: "WhatsApp recibido",
  sistema: "Sistema",
};

function CommentCard({ entry }: { entry: TimelineCommentEntry }) {
  const isCall = entry.kind === "llamada";
  const callId = entry.phoneCallId ?? null;
  // Solo ofrecemos play si la grabación está disponible (recordingStatus
  // === 'available'); el helper centraliza el gate para todos los paneles.
  const hasRecording = canPlayRecording(entry);
  const archived = entry.deletedAt != null;
  return (
    <CardShell>
      <CardHeader
        icon={isCall ? "📞" : "📝"}
        pill={{
          label: COMMENT_KIND_LABELS[entry.kind] ?? entry.kind,
          scheme: isCall ? "blue" : "purple",
        }}
        actor={entry.actorName ?? entry.actor}
        occurredAt={entry.occurredAt}
        extraPills={[
          ...(entry.pinnedAt && !archived
            ? [{ label: "Fijado", scheme: "amber" as PillScheme }]
            : []),
          ...(archived
            ? [{ label: "Archivada", scheme: "muted" as PillScheme }]
            : []),
        ]}
      />
      {entry.ticketSubject && (
        <div style={mutedLine}>Ticket: {entry.ticketSubject}</div>
      )}
      {entry.sharedFromLeadName && (
        <div style={mutedLine}>Registrada con {entry.sharedFromLeadName}</div>
      )}
      {entry.kind === "nota_interna" ? (
        <NoteEntryBody entry={entry} />
      ) : (
        entry.body && <div style={bodyText}>{entry.body}</div>
      )}
      {entry.callNote && (
        <div style={{ ...bodyText, color: "#495057" }}>
          Nota: {entry.callNote}
        </div>
      )}
      {hasRecording && callId && <RecordingPlayer callId={callId} />}
    </CardShell>
  );
}

/**
 * ¿Puede este usuario editar/archivar esta nota? Solo su AUTOR o un admin, y
 * solo `nota_interna` propias del lead. Espejo del guard del CRM
 * (`authorizeMutation` en comments.service) — el server manda; esto solo
 * decide si se pintan los botones.
 *
 * Las notas de TICKET quedan fuera: se gestionan desde la ficha del ticket,
 * que es donde vive su ciclo (y su endpoint de borrado).
 */
function canManageNote(
  entry: Pick<TimelineCommentEntry, "kind" | "actor" | "ticketId">,
  viewer: { email?: string; isAdmin: boolean },
): boolean {
  if (entry.kind !== "nota_interna" || entry.ticketId) return false;
  return viewer.isAdmin || (!!viewer.email && entry.actor === viewer.email);
}

/**
 * Cuerpo de una `nota_interna` del timeline: render de las capturas pegadas
 * (imágenes hidratadas por descarga autenticada) y del HTML si la nota es
 * ENRIQUECIDA, marca de «editada», banner de «archivada» y los controles de su
 * autor (editar / archivar / restaurar). Réplica del NoteEntry del CRM.
 *
 * Una nota ARCHIVADA solo llega aquí si el espectador es su autor o un admin
 * (el server la filtra para el resto): se pinta atenuada y etiquetada. La fila
 * y su historial (comment_activity_log) se conservan siempre.
 */
function NoteEntryBody({ entry }: { entry: TimelineCommentEntry }) {
  const leadUlid = useTimelineLeadUlid();
  const reload = useTimelineReload();
  const { me } = useCrmSession();
  const confirm = useConfirm();
  const isAdmin = me?.role === "admin";
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const attachments = useMemo(() => entry.attachments ?? [], [entry.attachments]);
  const fetchObjectUrl = useCallback(
    (attachmentId: string) =>
      fetchLeadCommentAttachmentObjectUrl(leadUlid, attachmentId),
    [leadUlid],
  );
  const picker = useAttachmentPicker();
  const note = useNoteComposer(picker, {
    persistedAttachments: attachments,
    fetchObjectUrl,
  });

  const archived = entry.deletedAt != null;
  const canManage = canManageNote(entry, { email: me?.email, isAdmin });
  // Adjuntos que NO están embebidos como imagen en el cuerpo → chips aparte.
  const looseAttachments = attachments.filter(
    (a) =>
      !a.contentType.startsWith("image/") ||
      (!entry.body.includes(a.filename) &&
        !(entry.bodyHtml ?? "").includes(a.filename)),
  );

  const describeMutationError = (e: unknown): string => {
    if (e instanceof CrmHttpError) {
      if (e.status === 403) {
        return "Solo el autor de la nota o un admin pueden modificarla.";
      }
      if (e.status === 409) {
        return "La nota está archivada: restáurala antes de editarla.";
      }
      return describeCrmError(e);
    }
    return "Error de conexión con el CRM.";
  };

  const startEditing = async () => {
    setErr(null);
    // Una nota rica abre directamente el editor con formato (hidratando sus
    // imágenes privadas); una sencilla abre el textarea de siempre.
    await note.load({ body: entry.body, bodyHtml: entry.bodyHtml ?? null });
    setEditing(true);
  };

  const save = async () => {
    if (pending) return;
    const payload = note.toPayload();
    if (!payload) return;
    setPending(true);
    setErr(null);
    try {
      // `attachments` solo lleva los NUEVOS: el CRM conserva los persistidos
      // y archiva el cuerpo previo en comment_activity_log (historial).
      await updateLeadComment(leadUlid, entry.commentId, payload);
      setEditing(false);
      note.reset();
      reload();
    } catch (e) {
      setErr(describeMutationError(e));
    } finally {
      setPending(false);
    }
  };

  const setArchivedState = async (next: boolean) => {
    if (pending) return;
    if (next) {
      const ok = await confirm({
        title: "Archivar nota",
        message:
          "Archivar NO borra la nota: la fila y su historial se conservan, y tú (o un admin) podéis restaurarla. Dejará de verse en el timeline del equipo.",
        tone: "danger",
        confirmLabel: "Archivar",
      });
      if (!ok) return;
    }
    setPending(true);
    setErr(null);
    try {
      await setLeadCommentArchived(leadUlid, entry.commentId, next);
      reload();
    } catch (e) {
      setErr(describeMutationError(e));
    } finally {
      setPending(false);
    }
  };

  if (editing) {
    return (
      <div style={{ marginTop: "0.3rem" }}>
        <NoteEditor
          state={note}
          picker={picker}
          rows={4}
          placeholder="Escribe la nota o pega una captura… (Ctrl+Enter para guardar)"
          disabled={pending}
          onSubmitShortcut={() => void save()}
        />
        <AttachmentPickerUI picker={picker} disabled={pending} />
        {err && <ErrorBanner>{err}</ErrorBanner>}
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
          <PrimaryButton
            onClick={() => void save()}
            disabled={pending || !note.hasContent}
          >
            {pending ? "Guardando…" : "Guardar"}
          </PrimaryButton>
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              note.reset();
            }}
            disabled={pending}
            style={noteActionBtn}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={archived ? { opacity: 0.6 } : undefined}>
      {archived && (
        <div style={{ marginTop: "0.2rem", fontSize: ".72rem", color: "#92400e" }}>
          Archivada por {entry.deletedBy ?? "—"}
          {entry.deletedAt ? ` · ${fmtDateTime(entry.deletedAt)}` : ""}
        </div>
      )}
      {entry.bodyHtml ? (
        <RichNoteBody
          html={entry.bodyHtml}
          attachments={attachments}
          fetchObjectUrl={fetchObjectUrl}
          style={archived ? { textDecoration: "line-through" } : undefined}
        />
      ) : entry.body ? (
        <NoteBody
          body={entry.body}
          attachments={attachments}
          fetchObjectUrl={fetchObjectUrl}
          style={{
            fontSize: ".82rem",
            ...(archived ? { textDecoration: "line-through" } : null),
          }}
        />
      ) : null}
      {looseAttachments.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: "0.4rem 0 0",
            padding: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: "0.4rem",
          }}
        >
          {looseAttachments.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => {
                  void fetchObjectUrl(a.id)
                    .then((url) => window.open(url, "_blank", "noopener"))
                    .catch(() => setErr("No se pudo descargar el adjunto"));
                }}
                style={attachmentChip}
                title={`${a.filename} (${formatBytes(a.sizeBytes)})`}
              >
                📎{" "}
                <span
                  style={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {a.filename}
                </span>
                <span style={{ color: "#73879C" }}>{formatBytes(a.sizeBytes)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {err && (
        <div style={{ marginTop: "0.25rem" }}>
          <ErrorBanner>{err}</ErrorBanner>
        </div>
      )}
      {(entry.editedAt || canManage) && (
        <div
          style={{
            marginTop: "0.25rem",
            display: "flex",
            alignItems: "center",
            gap: "0.6rem",
            fontSize: ".72rem",
            color: "#73879C",
          }}
        >
          {entry.editedAt && (
            <span title={fmtDateTime(entry.editedAt)}>Editada</span>
          )}
          {canManage && !archived && (
            <>
              <button
                type="button"
                onClick={() => void startEditing()}
                disabled={pending}
                style={noteLinkBtn}
              >
                Editar
              </button>
              <button
                type="button"
                onClick={() => void setArchivedState(true)}
                disabled={pending}
                style={{ ...noteLinkBtn, color: "#b32424" }}
              >
                Archivar
              </button>
            </>
          )}
          {canManage && archived && (
            <button
              type="button"
              onClick={() => void setArchivedState(false)}
              disabled={pending}
              style={{ ...noteLinkBtn, color: "#0069d9" }}
            >
              Restaurar
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const noteLinkBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "#73879C",
  padding: 0,
  fontSize: ".72rem",
  cursor: "pointer",
  textDecoration: "underline",
};

const noteActionBtn: React.CSSProperties = {
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "white",
  color: "#1f2933",
  padding: "0.35rem 0.75rem",
  fontSize: ".8rem",
  cursor: "pointer",
};

const attachmentChip: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  background: "white",
  color: "#1f2933",
  padding: "0.25rem 0.5rem",
  fontSize: ".76rem",
  cursor: "pointer",
};

function MoveCard({ entry }: { entry: TimelineMoveEntry }) {
  return (
    <CardShell>
      <CardHeader
        icon="↗"
        pill={{ label: "Movimiento", scheme: "blue" }}
        actor={entry.actorName ?? entry.actor}
        occurredAt={entry.occurredAt}
      />
      <div style={bodyText}>
        {entry.fromTagLabel ? `${entry.fromTagLabel} → ` : ""}
        <strong>{entry.toTagLabel ?? entry.toTag}</strong>
      </div>
    </CardShell>
  );
}

const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = {
  queued: "En cola",
  sent: "Enviado",
  delivered: "Entregado",
  opened: "Abierto",
  clicked: "Clic",
  bounced: "Rebotado",
  complained: "Spam",
  unsubscribed: "Baja",
  suppressed: "Bloqueado",
  received: "Recibido",
};

function EmailCard({ entry }: { entry: TimelineEmailEntry }) {
  const [open, setOpen] = useState(false);
  const subject = entry.subject?.trim() || "(sin asunto)";
  const dir = entry.direction === "inbound" ? "Entrante" : "Saliente";
  return (
    <CardShell>
      <CardHeader
        icon="✉"
        pill={{ label: `Email · ${dir}`, scheme: "amber" }}
        actor={entry.actorName ?? entry.actor}
        occurredAt={entry.occurredAt}
        extraPills={[
          {
            label: EMAIL_STATUS_LABELS[entry.status] ?? entry.status,
            scheme: "muted" as PillScheme,
          },
        ]}
      />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={linkBtn}
        aria-expanded={open}
      >
        {open ? "▾ " : "▸ "}
        {subject}
      </button>
      {open && (
        <>
          <div style={{ ...mutedLine, marginTop: "0.25rem" }}>
            {entry.direction === "inbound" && entry.fromAddress && (
              <div>De: {entry.fromAddress}</div>
            )}
            <div>Para: {entry.recipient}</div>
            {(entry.openCount > 0 || entry.clickCount > 0) && (
              <div>
                {entry.openCount} aperturas · {entry.clickCount} clics
              </div>
            )}
          </div>
          <EmailBody entry={entry} />
        </>
      )}
    </CardShell>
  );
}

// Cuerpo del email, pedido on-demand al expandir. Espejo del CRM: DOS fuentes —
// (1) hasBodyHtml → GET /html (body_html materializado: envíos del CRM);
// (2) hasArchive → GET /content-url (el `.eml` archivado: inbound Gmail /
// transaccional Mailgun) cuyo `parsed.html` trae el cuerpo. Si /html devuelve
// null y hay archivo, se cae a (2), igual que EmailStoredBody del CRM. El HTML
// se sanitiza (mismo criterio que el CRM) y se renderiza en un iframe sandbox
// aislado. Con la fuente (2) además ofrecemos "Descargar original" (URL firmada).
function EmailBody({ entry }: { entry: TimelineEmailEntry }) {
  const leadUlid = useTimelineLeadUlid();
  const hasAnyBody = entry.hasBodyHtml || entry.hasArchive;
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "empty" }
    | { status: "ready"; html: string; downloadUrl?: string }
    | { status: "error"; message: string }
  >(hasAnyBody ? { status: "loading" } : { status: "empty" });

  useEffect(() => {
    if (!hasAnyBody) {
      setState({ status: "empty" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });

    const fromArchive = () =>
      getEmailContentUrl(leadUlid, entry.emailId).then((res) => {
        if (cancelled) return;
        const clean = res.parsed?.html ? sanitizeEmailHtml(res.parsed.html) : "";
        setState(
          clean.trim()
            ? { status: "ready", html: clean, downloadUrl: res.url }
            : { status: "empty" },
        );
      });

    const load = entry.hasBodyHtml
      ? getEmailBodyHtml(leadUlid, entry.emailId).then((res) => {
          if (cancelled) return;
          const clean = res.html ? sanitizeEmailHtml(res.html) : "";
          if (clean.trim()) {
            setState({ status: "ready", html: clean });
          } else if (entry.hasArchive) {
            // Flag desincronizado: sin html materializado pero con .eml → cae
            // a la segunda fuente (mismo fallback que el CRM).
            return fromArchive();
          } else {
            setState({ status: "empty" });
          }
        })
      : fromArchive();

    load.catch((err) => {
      if (cancelled) return;
      const message =
        err instanceof CrmHttpError
          ? describeCrmError(err)
          : "No se pudo cargar el cuerpo del email";
      setState({ status: "error", message });
    });
    return () => {
      cancelled = true;
    };
  }, [leadUlid, entry.emailId, entry.hasBodyHtml, entry.hasArchive, hasAnyBody]);

  if (state.status === "loading") {
    return (
      <div style={{ ...mutedLine, marginTop: "0.4rem" }}>
        Cargando cuerpo del email…
      </div>
    );
  }
  if (state.status === "empty") {
    return (
      <div style={{ ...mutedLine, marginTop: "0.4rem", fontStyle: "italic" }}>
        Sin cuerpo disponible.
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div
        style={{
          marginTop: "0.4rem",
          fontSize: ".72rem",
          color: "#b32424",
        }}
      >
        {state.message}
      </div>
    );
  }
  return (
    <>
      <EmailHtmlFrame html={state.html} />
      {state.downloadUrl && (
        <a
          href={state.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: ".72rem", color: "#0069d9" }}
        >
          Descargar original (.eml) ↗
        </a>
      )}
    </>
  );
}

// Renderiza el HTML ya sanitizado en un iframe con sandbox. `allow-same-origin`
// SIN `allow-scripts`: el contenido sigue sin poder ejecutar nada (sanitizado +
// sin permiso de scripts), pero el padre sí puede medir su altura — con
// sandbox="" a secas contentDocument lanza y el email quedaba clavado en 120px
// con scroll interno minúsculo (bug 2026-07-14). Auto-ajusta hasta 900px.
function EmailHtmlFrame({ html }: { html: string }) {
  const [height, setHeight] = useState(160);
  const srcDoc = `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:0;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;font-size:13px;color:#1f2933;word-break:break-word;}img{max-width:100%;height:auto;}</style></head><body>${html}</body></html>`;
  return (
    <iframe
      title="Cuerpo del email"
      sandbox="allow-same-origin"
      srcDoc={srcDoc}
      onLoad={(e) => {
        try {
          const doc = e.currentTarget.contentDocument;
          if (doc) {
            const h = Math.max(
              doc.body.scrollHeight,
              doc.documentElement.scrollHeight,
            );
            if (h > 0) setHeight(Math.min(h + 16, 900));
          }
        } catch {
          // Si no podemos medir (navegador restrictivo), altura generosa fija.
          setHeight(480);
        }
      }}
      style={{
        marginTop: "0.5rem",
        width: "100%",
        height,
        border: "1px solid #e9ecef",
        borderRadius: "0.4rem",
        background: "white",
      }}
    />
  );
}


function ObservationCard({ entry }: { entry: TimelineObservationEntry }) {
  return (
    <CardShell>
      <CardHeader
        icon="🗒"
        pill={{ label: "Observación (Ops)", scheme: "muted" }}
        actor={entry.actorName ?? entry.actor}
        occurredAt={entry.occurredAt}
      />
      <div style={bodyText}>{entry.body}</div>
    </CardShell>
  );
}

function MilestoneCard({ entry }: { entry: TimelineMilestoneEntry }) {
  const origin = entry.leadSource ?? entry.awarenessSource;
  return (
    <CardShell>
      <CardHeader
        icon="🚩"
        pill={{ label: "Hito", scheme: "green" }}
        actor={null}
        occurredAt={entry.occurredAt}
      />
      <div style={bodyText}>
        Lead creado{origin ? ` · origen: ${origin}` : ""}
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Composer: añade una nota interna (POST /leads/:id/comments) y recarga.
// ---------------------------------------------------------------------------

function Composer({
  leadUlid,
  onCreated,
}: {
  leadUlid: string;
  onCreated: (comment: Comment) => void;
}) {
  const alert = useAlert();
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const text = body.trim();
    if (submitting || !text) return;
    setSubmitting(true);
    try {
      const created = await createLeadComment(leadUlid, {
        kind: "nota_interna",
        body: text,
      });
      setBody("");
      onCreated(created);
    } catch (err) {
      const msg =
        err instanceof CrmHttpError
          ? describeCrmError(err)
          : "No se pudo añadir la nota";
      await alert({
        title: "Error al añadir la nota",
        message: msg,
        tone: "danger",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginBottom: "0.8rem" }}>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Añadir nota interna…"
        rows={2}
        maxLength={4000}
        disabled={submitting}
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: "1px solid #dee2e6",
          borderRadius: "0.4rem",
          padding: "0.45rem 0.55rem",
          fontSize: ".82rem",
          fontFamily: "inherit",
          resize: "vertical",
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !body.trim()}
          style={{
            ...addBtn,
            marginTop: "0.4rem",
            opacity: submitting || !body.trim() ? 0.6 : 1,
          }}
        >
          {submitting ? "Añadiendo…" : "Añadir nota"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Primitivos de presentación.
// ---------------------------------------------------------------------------

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        border: "1px solid #e9ecef",
        borderRadius: "0.5rem",
        padding: "0.5rem 0.6rem",
        background: "white",
      }}
    >
      {children}
    </div>
  );
}

function CardHeader({
  icon,
  pill,
  actor,
  occurredAt,
  extraPills = [],
}: {
  icon: string;
  pill: { label: string; scheme: PillScheme };
  actor: string | null;
  occurredAt: string;
  extraPills?: { label: string; scheme: PillScheme }[];
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.35rem",
        flexWrap: "wrap",
        marginBottom: "0.3rem",
      }}
    >
      <span aria-hidden style={{ fontSize: ".85rem" }}>
        {icon}
      </span>
      <Pill label={pill.label} scheme={pill.scheme} />
      {extraPills.map((p) => (
        <Pill key={p.label} label={p.label} scheme={p.scheme} />
      ))}
      <div style={{ flex: 1 }} />
      <span style={{ color: "#73879C", fontSize: ".7rem" }}>
        {fmtDateTime(occurredAt)}
      </span>
      {actor && (
        <span style={{ color: "#495057", fontSize: ".7rem", width: "100%" }}>
          {actor}
        </span>
      )}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ color: "#73879C", fontSize: ".82rem" }}>{children}</div>;
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#fde2e2",
        color: "#b32424",
        padding: "0.5rem 0.7rem",
        borderRadius: "0.4rem",
        fontSize: ".82rem",
        marginBottom: "0.6rem",
      }}
    >
      {children}
    </div>
  );
}

const bodyText: React.CSSProperties = {
  fontSize: ".82rem",
  color: "#1f2933",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const mutedLine: React.CSSProperties = {
  fontSize: ".72rem",
  color: "#73879C",
};

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#1f2933",
  fontSize: ".82rem",
  fontWeight: 600,
  textAlign: "left",
  cursor: "pointer",
};

const addBtn: React.CSSProperties = {
  padding: "0.3rem 0.65rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".76rem",
  fontWeight: 600,
  cursor: "pointer",
};

const retryBtn: React.CSSProperties = {
  padding: "0.35rem 0.75rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#0069d9",
  border: "1px solid #dee2e6",
  fontSize: ".8rem",
  fontWeight: 600,
  cursor: "pointer",
};

const maximizeBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  padding: 0,
  borderRadius: "0.35rem",
  background: "white",
  color: "#495057",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};
